import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  lifecycle,
  withHandlers
} from 'recompose';
import { withRouter } from 'react-router';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Drawer from '@material-ui/core/Drawer';
import Header from '../../components/Header';
import MenuDrawer from '../../components/MenuDrawer';
import {
  isAuthenticated,
  sendLogin,
  changeEmalAddress,
  verifyEmailSubmit
} from '../../auth/Auth';
import {
  getAccounts,
  editAccountStatus,
  editAccountEmail
} from '../../actions/Accounts';
import { getShopDetail, editShop } from '../../actions/Shop';
import { getGroupDetail, editGroup } from '../../actions/Groups';
import ValidationText from '../../components/atoms/ValidationText';
import RequiredButton from '../../components/atoms/RequiredButton';
import ChangeButton from '../../components/atoms/ChangeButton';
import SendCodeButton from '../../components/atoms/SendCodeButton';
import ProgressDialog from '../../components/molecules/ProgressDialog';

const styles = {
  root: {
    backgroundColor: '#e0e0e0',
    flexGrow: 1,
    width: '100%'
  },
  pageTitileDiv: {
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  mainPaper: {
    position: 'relative',
    margin: 1,
    padding: 10
  },
  zoom: {
    position: 'relative',
    left: -10,
    top: 20
  },
  mainPaperTitleDiv: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '20px 26px 10px 26px'
  },
  searchForm: {
    padding: '10px 26px 10px 26px'
  },
  textField: {
    padding: '0px 5px 0px 5px'
  },
  button: {
    margin: '5px 5px'
  },
  buttonLeft: {
    textAlign: 'left'
    // CSV出力ボタンを実装時にコメントアウトを外す
    // float: 'left'
  },
  buttonGroupLeft: {
    textAlign: 'left',
    float: 'left'
  },
  buttonRight: {
    textAlign: 'right'
  },
  buttonGroupRight: {
    textAlign: 'right'
  },
  editTable: {
    width: 'auto'
  },
  tableCell90: {
    width: '90%'
  },
  tableCellBorderNone: {
    borderBottom: 'none'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account
    }),
    dispatch => ({
      actions: bindActionCreators(
        {
          getAccounts,
          editAccountStatus,
          getShopDetail,
          editShop,
          getGroupDetail,
          editGroup,
          editAccountEmail
        },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('openDrawer', 'updateOpenDrawer', false),
  withState('openProgress', 'updateOpenProgress', false),
  withState('loading', 'updateLoading', false),
  withState('isChangeSuccess', 'updateIsChangeSuccess', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('redirectPath', 'setRedirectPath', '/shops'),
  withState('oldPassword', 'setOldPassword', ''),
  withState('password', 'setPassword', ''),
  withState('passwordError', 'setPasswordError', ''),
  withState('confirmPassword', 'setConfirmPassword', ''),
  withState('confirmPasswordError', 'setConfirmPasswordError', ''),
  withState('phone', 'setPhone', ''),
  withState('phoneAuthCode', 'setPhoneAuthCode', ''),
  withState('email', 'setEmail', ''),
  withState('emailError', 'setEmailError', ''),
  withState('emailAuthCode', 'setEmailAuthCode', ''),
  withState('emailAuthCodeError', 'setEmailAuthCodeError', ''),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    handleRedirect: props => path => {
      props.history.push(path);
    }
  }),
  lifecycle({
    componentDidMount() {
      isAuthenticated(this.props);
      this.props.actions.getAccounts({ cognitoUserId: '' }).then(data => {
        this.props.checkSession(data);
      });
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    handleRedirect,
    account,
    openDrawer,
    updateOpenDrawer,
    openProgress,
    updateOpenProgress,
    loading,
    updateLoading,
    isChangeSuccess,
    updateIsChangeSuccess,
    resultMessage,
    setResultMessage,
    redirectPath,
    setRedirectPath,
    email,
    setEmail,
    emailAuthCode,
    setEmailAuthCode,
    emailError,
    setEmailError,
    emailAuthCodeError,
    setEmailAuthCodeError
  }) => {
    const isReleased =
      account && account.accountStatus && account.accountStatus.status === true;

    const errors = {
      emailError,
      emailAuthCodeError
    };
    const validEmail = () => {
      const value = email;
      if (value === '') {
        setEmailError('必須項目です。');
        return 1;
      }
      setEmailError('');
      setEmail(value);
      return 0;
    };
    const validEmailAuthCode = () => {
      const value = emailAuthCode;
      if (value === '') {
        setEmailAuthCodeError('必須項目です。');
        return 1;
      }
      setEmailAuthCodeError('');
      setEmailAuthCode(value);
      return 0;
    };
    const valid = () => {
      const errorCount = validEmail() + validEmailAuthCode();
      if (errorCount === 0) {
        return true;
      }
      return false;
    };

    const sendCodeAction = () => {
      if (validEmail() !== 0) {
        alert('メールアドレスを正しく入力してください。');
      } else {
        updateLoading(true);
        updateOpenProgress(true);
        changeEmalAddress(email)
          .then(async () => {
            let result = false;
            if (account.role === 'SHOP') {
              const detailData = await actions.getShopDetail(account.shopId);
              detailData.payload.email = email;
              const editShopResult = await actions
                .editShop(account.shopId, detailData.payload)
                .then(data => checkSession(data));
              const editEmailResult = await actions.editAccountEmail(
                account.manageAccountId,
                {
                  shopId: account.shopId,
                  email
                }
              );
              if (
                editShopResult.type === 'EDIT_SHOP_SUCCESS' &&
                editEmailResult.type === 'EDIT_ACCOUNT_EMAIL_SUCCESS'
              ) {
                result = true;
              }
            } else if (account.role === 'GROUP') {
              const detailData = await actions.getGroupDetail(account.groupId);
              detailData.payload.email = email;
              const editGroupResult = await actions
                .editGroup(account.groupId, detailData.payload)
                .then(data => checkSession(data));
              const editEmailResult = await actions.editAccountEmail(
                account.manageAccountId,
                {
                  groupId: account.groupId,
                  email
                }
              );
              if (
                editGroupResult.type === 'EDIT_GROUP_SUCCESS' &&
                editEmailResult.type === 'EDIT_ACCOUNT_EMAIL_SUCCESS'
              ) {
                result = true;
              }
            } else {
              const editEmailResult = await actions.editAccountEmail(
                account.manageAccountId,
                {
                  email
                }
              );
              if (editEmailResult.type === 'EDIT_ACCOUNT_EMAIL_SUCCESS') {
                result = true;
              }
            }

            if (result) {
              setResultMessage(
                '認証コードを送信しました。メールをご確認ください。'
              );
              updateLoading(false);
            } else {
              setResultMessage('認証コード送信に失敗しました。');
              updateLoading(false);
            }
          })
          .catch(() => {
            updateIsChangeSuccess(false);
            setResultMessage('認証コード送信に失敗しました。');
            updateLoading(false);
          });
      }
    };
    const changeAction = async () => {
      if (!valid()) {
        alert('登録情報を正しく入力してください。');
      } else {
        updateLoading(true);
        updateOpenProgress(true);
        await actions
          .getAccounts({ cognitoUserId: '' })
          .then(data => checkSession(data));
        if (account.role === 'GROUP') {
          setRedirectPath('/groups_group');
        } else if (isReleased !== null && !isReleased) {
          setRedirectPath('/release_step');
        }
        await verifyEmailSubmit(emailAuthCode)
          .then(async () => {
            updateIsChangeSuccess(true);
            setResultMessage('メールアドレスを変更しました。');
            updateLoading(false);
          })
          .catch(() => {
            updateIsChangeSuccess(false);
            setResultMessage('メールアドレス変更に失敗しました。');
            updateLoading(false);
          });
      }
    };

    const progressOkAction = () => {
      if (isChangeSuccess) {
        updateOpenProgress(false);
        handleRedirect(redirectPath);
      } else {
        updateOpenProgress(false);
      }
    };

    return (
      <div className={classes.root}>
        <Header
          onMenuClick={() => {
            updateOpenDrawer(true);
          }}
        />
        <Drawer
          open={openDrawer}
          anchor="left"
          onClose={() => {
            updateOpenDrawer(false);
          }}
        >
          <div tabIndex={0} role="button">
            <MenuDrawer account={account} />
          </div>
        </Drawer>
        <div className={classes.pageTitileDiv}>
          <Typography variant="h6" color="textSecondary">
            メールアドレス変更
          </Typography>
        </div>
        <Paper className={classes.mainPaper} elevation={1}>
          <div className={classes.searchForm}>
            <Table className={classes.editTable}>
              <TableRow>
                <TableCell className={classes.tableCellBorderNone}>
                  <RequiredButton />
                </TableCell>
                <TableCell
                  className={classNames(
                    classes.tableCellBorderNone,
                    classes.tableCell90
                  )}
                >
                  <TextField
                    required
                    label="メールアドレス"
                    placeholder="gochimeshi@gochi.com"
                    type="email"
                    fullWidth
                    className={classes.textField}
                    margin="normal"
                    onChange={e => {
                      setEmail(e.target.value);
                    }}
                    onBlur={validEmail}
                  />
                  <ValidationText message={errors.emailError} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableCellBorderNone} />
                <TableCell className={classes.tableCellBorderNone}>
                  <SendCodeButton onClickAction={sendCodeAction} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableCellBorderNone}>
                  <RequiredButton />
                </TableCell>
                <TableCell className={classes.tableCellBorderNone}>
                  <TextField
                    required
                    label="認証コード"
                    fullWidth
                    className={classes.textField}
                    margin="normal"
                    onChange={e => {
                      setEmailAuthCode(e.target.value);
                    }}
                    onBlur={validEmailAuthCode}
                  />
                  <ValidationText message={errors.emailAuthCodeError} />
                </TableCell>
              </TableRow>
            </Table>
          </div>
          <div>
            <ChangeButton onClickAction={changeAction} />
          </div>
          <ProgressDialog
            openProgress={openProgress}
            progressOkAction={progressOkAction}
            loading={loading}
            resultMessage={resultMessage}
          />
        </Paper>
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
